import React, { useState, useEffect } from 'react';
import { customerAPI, customerTimesheetAPI, purchaseOrderAPI, CustomerTimesheetParams } from '../services/api';
import type { Customer, CustomerTimesheetPreview, DateRangeType, TimesheetUserSummary } from '../types';
import type { PurchaseOrder } from '../types';

type TimesheetScope = 'purchase_order' | 'company';

const CustomerTimesheetReport: React.FC = () => {
  const [scope, setScope] = useState<TimesheetScope>('purchase_order');
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [purchaseOrders, setPurchaseOrders] = useState<PurchaseOrder[]>([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState<number | null>(null);
  const [selectedPOId, setSelectedPOId] = useState<number | null>(null);
  const [dateRangeType, setDateRangeType] = useState<DateRangeType>('current_month');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [preview, setPreview] = useState<CustomerTimesheetPreview | null>(null);
  const [expandedUsers, setExpandedUsers] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchCustomers();
    fetchPurchaseOrders();
  }, []);

  useEffect(() => {
    // Set default dates based on date range type
    const today = new Date();
    
    if (dateRangeType === 'current_month') {
      const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
      const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      setStartDate(firstDay.toISOString().split('T')[0]);
      setEndDate(lastDay.toISOString().split('T')[0]);
    } else if (dateRangeType === 'previous_month') {
      const firstDay = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const lastDay = new Date(today.getFullYear(), today.getMonth(), 0);
      setStartDate(firstDay.toISOString().split('T')[0]);
      setEndDate(lastDay.toISOString().split('T')[0]);
    }
  }, [dateRangeType]);

  const fetchCustomers = async () => {
    try {
      const response = await customerAPI.getAll();
      setCustomers(response.data.results);
    } catch (err) {
      console.error('Failed to fetch customers:', err);
      setError('Failed to load customers');
    }
  };

  const fetchPurchaseOrders = async () => {
    try {
      const response = await purchaseOrderAPI.getAll();
      setPurchaseOrders(response.data.results ?? []);
    } catch (err) {
      console.error('Failed to fetch purchase orders:', err);
      setError('Failed to load purchase orders');
    }
  };

  const buildParams = (): CustomerTimesheetParams => {
    const params: CustomerTimesheetParams = { date_range_type: dateRangeType };
    if (scope === 'purchase_order' && selectedPOId != null) {
      params.purchase_order_id = selectedPOId;
    } else if (scope === 'company' && selectedCustomerId != null) {
      params.customer_id = selectedCustomerId;
    }
    if (dateRangeType === 'custom') {
      params.start_date = startDate;
      params.end_date = endDate;
    }
    return params;
  };

  const hasValidSelection = () =>
    (scope === 'purchase_order' && selectedPOId != null) ||
    (scope === 'company' && selectedCustomerId != null);

  const handlePreview = async () => {
    if (scope === 'purchase_order' && !selectedPOId) {
      setError('Please select a purchase order');
      return;
    }
    if (scope === 'company' && !selectedCustomerId) {
      setError('Please select a company');
      return;
    }
    if (dateRangeType === 'custom' && (!startDate || !endDate)) {
      setError('Please select start and end dates for custom range');
      return;
    }

    setLoading(true);
    setError(null);
    setPreview(null);

    try {
      const response = await customerTimesheetAPI.preview(buildParams());
      setPreview(response.data);
    } catch (err: any) {
      console.error('Failed to fetch preview:', err);
      setError(err.response?.data?.error || 'Failed to generate preview');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    if (!hasValidSelection()) {
      setError(scope === 'purchase_order' ? 'Please select a purchase order' : 'Please select a company');
      return;
    }

    setExporting(true);
    setError(null);

    try {
      await customerTimesheetAPI.export(buildParams());
    } catch (err: any) {
      console.error('Failed to export:', err);
      setError(err.message || 'Failed to export timesheet');
    } finally {
      setExporting(false);
    }
  };

  const toggleUserExpanded = (userId: number) => {
    setExpandedUsers((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) {
        next.delete(userId);
      } else {
        next.add(userId);
      }
      return next;
    });
  };

  const formatCurrency = (value: string) => {
    const num = parseFloat(value);
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(num);
  };

  const formatHours = (value: string) => {
    return parseFloat(value).toFixed(2);
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    });
  };

  const renderUserSummary = (user: TimesheetUserSummary) => {
    const isExpanded = expandedUsers.has(user.user_id);

    return (
      <div key={user.user_id} className="user-summary-card">
        <div
          className="user-header"
          onClick={() => toggleUserExpanded(user.user_id)}
          style={{ cursor: 'pointer' }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <span style={{ fontSize: '1.2rem' }}>{isExpanded ? '▼' : '▶'}</span>
            <h4 style={{ margin: 0 }}>{user.user_name}</h4>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontWeight: 'bold' }}>{formatHours(user.total_hours)} hours</div>
            <div style={{ color: '#27ae60' }}>{formatCurrency(user.total_cost)}</div>
          </div>
        </div>

        {/* Project breakdown - always visible */}
        <div className="table-container" style={{ marginTop: '1rem' }}>
          <table style={{ fontSize: '0.9rem' }}>
            <thead>
              <tr>
                <th>Project</th>
                <th>Rate</th>
                <th style={{ textAlign: 'right' }}>Hours</th>
                <th style={{ textAlign: 'right' }}>Cost</th>
              </tr>
            </thead>
            <tbody>
              {user.projects.map((project) => (
                <tr key={project.project_code}>
                  <td>{project.project_code}</td>
                  <td>{project.rate_name} ({formatCurrency(project.rate_amount)}/hr)</td>
                  <td style={{ textAlign: 'right' }}>{formatHours(project.hours)}</td>
                  <td style={{ textAlign: 'right' }}>{formatCurrency(project.cost)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Detailed entries - expanded view */}
        {isExpanded && user.entries.length > 0 && (
          <div style={{ marginTop: '1rem', padding: '1rem', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
            <h5 style={{ marginTop: 0, marginBottom: '0.5rem' }}>Daily Entries</h5>
            <div className="table-container">
              <table style={{ fontSize: '0.85rem' }}>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Project</th>
                    <th style={{ textAlign: 'right' }}>Hours</th>
                    <th>Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {user.entries.map((entry, idx) => (
                    <tr key={idx}>
                      <td>{formatDate(entry.date)}</td>
                      <td>{entry.project_code}</td>
                      <td style={{ textAlign: 'right' }}>{formatHours(entry.hours)}</td>
                      <td style={{ maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {entry.notes || '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="container">
      <div className="card">
        <h2>Customer Timesheet Report</h2>
        <p style={{ color: '#7f8c8d', marginBottom: '1.5rem' }}>
          Generate and export timesheets grouped by company for invoicing.
        </p>

        {/* Filters */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
          <div className="form-group">
            <label htmlFor="scope">Scope</label>
            <select
              id="scope"
              value={scope}
              onChange={(e) => {
                setScope(e.target.value as TimesheetScope);
                setPreview(null);
              }}
            >
              <option value="purchase_order">By Purchase Order</option>
              <option value="company">By Company</option>
            </select>
          </div>

          {scope === 'purchase_order' && (
            <div className="form-group">
              <label htmlFor="purchase_order">Purchase Order *</label>
              <select
                id="purchase_order"
                value={selectedPOId ?? ''}
                onChange={(e) => {
                  setSelectedPOId(e.target.value ? parseInt(e.target.value) : null);
                  setPreview(null);
                }}
              >
                <option value="">-- Select PO --</option>
                {purchaseOrders.map((po) => (
                  <option key={po.id} value={po.id}>
                    {po.po_number} {po.customer_details?.name ? `(${po.customer_details.name})` : ''}
                  </option>
                ))}
              </select>
            </div>
          )}

          {scope === 'company' && (
            <div className="form-group">
              <label htmlFor="customer">Company *</label>
              <select
                id="customer"
                value={selectedCustomerId ?? ''}
                onChange={(e) => {
                  setSelectedCustomerId(e.target.value ? parseInt(e.target.value) : null);
                  setPreview(null);
                }}
              >
                <option value="">-- Select Company --</option>
                {customers.map((customer) => (
                  <option key={customer.id} value={customer.id}>
                    {customer.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="form-group">
            <label htmlFor="dateRangeType">Date Range</label>
            <select
              id="dateRangeType"
              value={dateRangeType}
              onChange={(e) => setDateRangeType(e.target.value as DateRangeType)}
            >
              <option value="current_month">Current Month</option>
              <option value="previous_month">Previous Month</option>
              <option value="custom">Custom Range</option>
            </select>
          </div>

          {dateRangeType === 'custom' && (
            <>
              <div className="form-group">
                <label htmlFor="startDate">Start Date</label>
                <input
                  type="date"
                  id="startDate"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </div>

              <div className="form-group">
                <label htmlFor="endDate">End Date</label>
                <input
                  type="date"
                  id="endDate"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </div>
            </>
          )}
        </div>

        {/* Action Buttons */}
        <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem' }}>
          <button
            onClick={handlePreview}
            className="btn btn-primary"
            disabled={loading || !hasValidSelection()}
          >
            {loading ? 'Loading...' : 'Preview'}
          </button>

          <button
            onClick={handleExport}
            className="btn btn-secondary"
            disabled={exporting || !hasValidSelection() || !preview}
          >
            {exporting ? 'Exporting...' : 'Export to Excel'}
          </button>
        </div>

        {/* Error Display */}
        {error && (
          <div className="alert alert-danger" style={{ marginBottom: '1.5rem' }}>
            {error}
          </div>
        )}
      </div>

      {/* Preview Results */}
      {preview && (
        <div className="card" style={{ marginTop: '1.5rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
            <div>
              <h3 style={{ margin: 0 }}>
                {preview.customer_name}
                {preview.po_number != null && preview.po_number !== '' && (
                  <span style={{ fontWeight: 'normal', color: '#7f8c8d' }}> — PO: {preview.po_number}</span>
                )}
              </h3>
              <p style={{ margin: '0.25rem 0 0', color: '#7f8c8d' }}>
                {formatDate(preview.start_date)} — {formatDate(preview.end_date)}
              </p>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>{formatHours(preview.grand_total_hours)} hours</div>
              <div style={{ fontSize: '1.25rem', color: '#27ae60' }}>{formatCurrency(preview.grand_total_cost)}</div>
            </div>
          </div>

          {preview.users.length === 0 ? (
            <p style={{ color: '#7f8c8d', textAlign: 'center', padding: '2rem' }}>
              No time entries found for the selected company and date range.
            </p>
          ) : (
            <>
              {/* Hours Matrix - Projects by Employees */}
              <h4 style={{ marginBottom: '1rem', borderBottom: '1px solid #ecf0f1', paddingBottom: '0.5rem' }}>
                Hours by Project & Employee
              </h4>
              <div className="table-container" style={{ overflowX: 'auto' }}>
                <table className="matrix-table">
                  <thead>
                    <tr>
                      <th style={{ position: 'sticky', left: 0, background: '#f8f9fa', zIndex: 1 }}>Project</th>
                      {preview.users.map((user) => (
                        <th key={user.user_id} style={{ textAlign: 'center', minWidth: '120px' }}>
                          {user.user_name}
                        </th>
                      ))}
                      <th style={{ textAlign: 'right', fontWeight: 'bold', background: '#e8f4f8', minWidth: '100px' }}>
                        Project Total
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.project_totals.map((project) => {
                      // Calculate hours per employee for this project
                      const employeeHours = preview.users.map((user) => {
                        const userProject = user.projects.find(p => p.project_code === project.project_code);
                        return userProject ? parseFloat(userProject.hours) : 0;
                      });
                      
                      return (
                        <tr key={project.project_code}>
                          <td style={{ position: 'sticky', left: 0, background: 'white', fontWeight: 500 }}>
                            {project.project_code}
                          </td>
                          {employeeHours.map((hours, idx) => (
                            <td key={idx} style={{ textAlign: 'center' }}>
                              {hours > 0 ? formatHours(hours.toString()) : '—'}
                            </td>
                          ))}
                          <td style={{ textAlign: 'right', fontWeight: 'bold', background: '#e8f4f8' }}>
                            {formatHours(project.hours)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr style={{ fontWeight: 'bold', background: '#e8f4f8' }}>
                      <td style={{ position: 'sticky', left: 0, background: '#e8f4f8' }}>Employee Total</td>
                      {preview.users.map((user) => (
                        <td key={user.user_id} style={{ textAlign: 'center' }}>
                          {formatHours(user.total_hours)}
                        </td>
                      ))}
                      <td style={{ textAlign: 'right', fontSize: '1.1rem' }}>
                        {formatHours(preview.grand_total_hours)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              {/* Cost Summary - Side by side tables */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', gap: '2rem', marginTop: '2rem' }}>
                {/* Project Totals */}
                <div>
                  <h4 style={{ marginBottom: '1rem', borderBottom: '1px solid #ecf0f1', paddingBottom: '0.5rem' }}>
                    Project Totals
                  </h4>
                  <div className="table-container">
                    <table>
                      <thead>
                        <tr>
                          <th>Project</th>
                          <th style={{ textAlign: 'right' }}>Hours</th>
                          <th style={{ textAlign: 'right' }}>Cost</th>
                        </tr>
                      </thead>
                      <tbody>
                        {preview.project_totals.map((project) => (
                          <tr key={project.project_code}>
                            <td>{project.project_code}</td>
                            <td style={{ textAlign: 'right' }}>{formatHours(project.hours)}</td>
                            <td style={{ textAlign: 'right' }}>{formatCurrency(project.cost)}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr style={{ fontWeight: 'bold', backgroundColor: '#f8f9fa' }}>
                          <td>Total</td>
                          <td style={{ textAlign: 'right' }}>{formatHours(preview.grand_total_hours)}</td>
                          <td style={{ textAlign: 'right' }}>{formatCurrency(preview.grand_total_cost)}</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>

                {/* Employee Totals */}
                <div>
                  <h4 style={{ marginBottom: '1rem', borderBottom: '1px solid #ecf0f1', paddingBottom: '0.5rem' }}>
                    Employee Totals
                  </h4>
                  <div className="table-container">
                    <table>
                      <thead>
                        <tr>
                          <th>Employee</th>
                          <th style={{ textAlign: 'right' }}>Hours</th>
                          <th style={{ textAlign: 'right' }}>Cost</th>
                        </tr>
                      </thead>
                      <tbody>
                        {preview.users.map((user) => (
                          <tr key={user.user_id}>
                            <td>{user.user_name}</td>
                            <td style={{ textAlign: 'right' }}>{formatHours(user.total_hours)}</td>
                            <td style={{ textAlign: 'right' }}>{formatCurrency(user.total_cost)}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr style={{ fontWeight: 'bold', backgroundColor: '#f8f9fa' }}>
                          <td>Total</td>
                          <td style={{ textAlign: 'right' }}>{formatHours(preview.grand_total_hours)}</td>
                          <td style={{ textAlign: 'right' }}>{formatCurrency(preview.grand_total_cost)}</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              </div>

              {/* Detailed Employee Entries - Expandable */}
              <h4 style={{ marginTop: '2rem', marginBottom: '1rem', borderBottom: '1px solid #ecf0f1', paddingBottom: '0.5rem' }}>
                Employee Details
              </h4>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '1rem' }}>
                {preview.users.map(renderUserSummary)}
              </div>
            </>
          )}
        </div>
      )}

      <style>{`
        .user-summary-card {
          background: white;
          border: 1px solid #ecf0f1;
          border-radius: 8px;
          padding: 1rem;
        }
        
        .user-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        
        .user-header:hover {
          background-color: #f8f9fa;
          margin: -1rem;
          padding: 1rem;
          border-radius: 8px;
        }
        
        .alert-danger {
          background-color: #fee;
          color: #c00;
          padding: 1rem;
          border-radius: 4px;
          border: 1px solid #fcc;
        }
        
        .matrix-table {
          border-collapse: collapse;
          width: 100%;
        }
        
        .matrix-table th,
        .matrix-table td {
          padding: 0.75rem 1rem;
          border: 1px solid #e0e0e0;
        }
        
        .matrix-table th {
          background: #f8f9fa;
          font-weight: 600;
          white-space: nowrap;
        }
        
        .matrix-table tbody tr:hover {
          background-color: #f5f5f5;
        }
        
        .matrix-table tfoot td {
          border-top: 2px solid #ccc;
        }
      `}</style>
    </div>
  );
};

export default CustomerTimesheetReport;

